import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  CheckCircle2,
  Copy,
  Clock,
  ShoppingBag,
  Truck,
  ShieldCheck,
  Percent,
  ArrowRight,
} from 'lucide-react';

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

const LAUNCH_IMAGE_URL = '/images/MarketMatemm.png';

const getTargetTimestamp = (): number => {
  const now = new Date();
  let targetYear = now.getFullYear();
  let target = new Date(targetYear, 8, 4, 19, 0, 0);

  if (now.getTime() >= target.getTime()) {
    target = new Date(targetYear + 1, 8, 4, 19, 0, 0);
  }

  return target.getTime();
};

const FlipCardUnit: React.FC<{ value: number; label: string; animateKey: number }> = ({
  value,
  label,
  animateKey,
}) => {
  const formatted = String(value).padStart(2, '0');

  return (
    <div className="flex flex-col items-center">
      <div className="relative group">

        <div className="absolute -inset-1 bg-gradient-to-r from-emerald-500 to-emerald-700 rounded-2xl blur-md opacity-30 group-hover:opacity-60 transition duration-500"></div>

        <div className="relative w-18 sm:w-24 md:w-28 h-20 sm:h-28 md:h-32 bg-slate-900 border border-emerald-500/30 rounded-2xl shadow-2xl flex items-center justify-center overflow-hidden">
          <div className="absolute inset-x-0 top-1/2 h-[1px] bg-slate-950/80 shadow-xs z-10"></div>

          <div className="absolute inset-x-0 top-0 h-1/2 bg-gradient-to-b from-white/10 to-transparent"></div>
          <span
            key={animateKey}
            className="font-black text-3xl sm:text-5xl md:text-6xl text-emerald-400 font-mono tracking-tight animate-flip-digit drop-shadow-[0_4px_10px_rgba(5,150,105,0.4)]"
          >
            {formatted}
          </span>
        </div>
      </div>

      <span className="mt-2.5 text-[11px] sm:text-xs font-extrabold uppercase tracking-widest text-emerald-300/80">
        {label}
      </span>
    </div>
  );
};

export const Launch: React.FC = () => {
  const [timeLeft, setTimeLeft] = useState<TimeLeft>({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const targetTime = getTargetTimestamp();

    const updateCountdown = () => {
      const now = new Date().getTime();
      const difference = targetTime - now;

      if (difference <= 0) {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        return;
      }

      const days = Math.floor(difference / (1000 * 60 * 60 * 24));
      const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((difference % (1000 * 60)) / 1000);

      setTimeLeft({ days, hours, minutes, seconds });
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, []);

  const handleCopyLink = () => {
    const launchUrl = window.location.origin + '/launch';
    navigator.clipboard.writeText(launchUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 3000);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-950 via-slate-900 to-slate-950 text-slate-100 flex flex-col justify-between selection:bg-emerald-500 selection:text-white relative overflow-hidden pt-28 pb-16 px-4 sm:px-6 lg:px-8">
      <div className="absolute top-0 inset-x-0 h-[520px] bg-gradient-to-b from-emerald-900/40 via-emerald-950/20 to-transparent pointer-events-none"></div>
      <div className="absolute -top-10 -left-10 w-[600px] h-[600px] bg-emerald-400/35 blur-[130px] rounded-full pointer-events-none"></div>
      <div className="absolute top-0 left-8 w-[420px] h-[420px] bg-teal-300/30 blur-[90px] rounded-full pointer-events-none"></div>
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-7xl h-96 bg-emerald-500/25 blur-[120px] rounded-full pointer-events-none"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-emerald-900/30 blur-[140px] rounded-full pointer-events-none"></div>

      <div className="max-w-4xl mx-auto w-full space-y-12 relative z-10">
        <div className="flex flex-col items-center justify-center gap-4">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-950/80 border border-emerald-500/40 backdrop-blur-md shadow-lg shadow-emerald-950/50">
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"></span>
            </span>
            <span className="text-xs font-bold text-emerald-300 tracking-wide uppercase">
              VIP Early Access Confirmed
            </span>
          </div>
        </div>

        <div className="text-center space-y-4 max-w-2xl mx-auto">
          <h1 className="text-3xl sm:text-5xl lg:text-6xl font-black text-white tracking-tight leading-tight">
            You're On The List! <br />
            <span className="bg-gradient-to-r from-emerald-400 via-emerald-300 to-teal-200 bg-clip-text text-transparent">
              Sit Back & Relax
            </span>
          </h1>
          <p className="text-slate-300 text-sm sm:text-base leading-relaxed font-medium">
            Your spot is officially reserved. Our team is finalizing direct farm-to-table delivery routes and onboarding verified local merchants for your city.
          </p>
        </div>

        <div className="relative rounded-3xl overflow-hidden border border-emerald-500/30 bg-slate-900/60 backdrop-blur-md shadow-2xl p-2 sm:p-4 group">
          <div className="relative rounded-2xl overflow-hidden w-full flex items-center justify-center bg-slate-950/80">
            <img
              src={LAUNCH_IMAGE_URL}
              alt="MarketMate Launch Notice"
              className="w-full h-auto max-h-[650px] object-contain group-hover:scale-[1.01] transition-transform duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-950/90 via-transparent to-transparent pointer-events-none"></div>
            <div className="absolute bottom-4 left-4 right-4 sm:bottom-6 sm:left-6 flex justify-between items-end z-10">
              <div>
                <p className="text-xs font-bold text-emerald-400 uppercase tracking-widest">Official Status</p>
                <p className="text-lg sm:text-xl font-bold text-white">MarketMate App Opening Soon</p>
              </div>
              <div className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-slate-900/90 text-emerald-300 text-xs font-semibold backdrop-blur-xs border border-emerald-500/30">
                <span>Priority Allocation</span>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-slate-900/80 border border-emerald-500/30 backdrop-blur-xl rounded-3xl p-6 sm:p-10 shadow-2xl space-y-6 text-center">
          <div className="space-y-1">
            <div className="flex items-center justify-center gap-2 text-emerald-400">
              <Clock className="w-5 h-5 animate-pulse" />
              <h2 className="text-sm font-extrabold uppercase tracking-widest">
                Early Access Countdown
              </h2>
            </div>
            <p className="text-xs text-slate-400 font-medium">
              Time remaining until early access doors open in your area:
            </p>
          </div>

          <div className="grid grid-cols-4 gap-3 sm:gap-6 max-w-xl mx-auto py-2">
            <FlipCardUnit value={timeLeft.days} label="Days" animateKey={timeLeft.days} />
            <FlipCardUnit value={timeLeft.hours} label="Hours" animateKey={timeLeft.hours} />
            <FlipCardUnit value={timeLeft.minutes} label="Minutes" animateKey={timeLeft.minutes} />
            <FlipCardUnit value={timeLeft.seconds} label="Seconds" animateKey={timeLeft.seconds} />
          </div>

          <p className="text-xs text-slate-400 italic">
            All registered waitlist members will receive priority notification emails when countdown finishes.
          </p>
        </div>

        <div className="space-y-4">
          <h3 className="text-center text-xs font-extrabold uppercase tracking-widest text-emerald-400">
            What You'll Enjoy At Launch
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-slate-900/60 border border-slate-800 p-5 rounded-2xl space-y-2 hover:border-emerald-500/40 transition-colors">
              <div className="w-10 h-10 rounded-xl bg-emerald-500/10 text-emerald-400 flex items-center justify-center border border-emerald-500/20">
                <ShoppingBag className="w-5 h-5" />
              </div>
              <h4 className="font-bold text-sm text-white">Farm-Gate Prices</h4>
              <p className="text-xs text-slate-400 leading-relaxed">
                Direct fresh foodstuff orders from local verified farmers without market markup.
              </p>
            </div>

            <div className="bg-slate-900/60 border border-slate-800 p-5 rounded-2xl space-y-2 hover:border-emerald-500/40 transition-colors">
              <div className="w-10 h-10 rounded-xl bg-emerald-500/10 text-emerald-400 flex items-center justify-center border border-emerald-500/20">
                <Truck className="w-5 h-5" />
              </div>
              <h4 className="font-bold text-sm text-white">Priority Delivery</h4>
              <p className="text-xs text-slate-400 leading-relaxed">
                Dedicated delivery slots reserved first for early access waitlist members.
              </p>
            </div>

            <div className="bg-slate-900/60 border border-slate-800 p-5 rounded-2xl space-y-2 hover:border-emerald-500/40 transition-colors">
              <div className="w-10 h-10 rounded-xl bg-emerald-500/10 text-emerald-400 flex items-center justify-center border border-emerald-500/20">
                <Percent className="w-5 h-5" />
              </div>
              <h4 className="font-bold text-sm text-white">Launch Coupons</h4>
              <p className="text-xs text-slate-400 leading-relaxed">
                Exclusive delivery discounts automatically credited to your email on launch day.
              </p>
            </div>

            <div className="bg-slate-900/60 border border-slate-800 p-5 rounded-2xl space-y-2 hover:border-emerald-500/40 transition-colors">
              <div className="w-10 h-10 rounded-xl bg-emerald-500/10 text-emerald-400 flex items-center justify-center border border-emerald-500/20">
                <ShieldCheck className="w-5 h-5" />
              </div>
              <h4 className="font-bold text-sm text-white">0% Merchant Fees</h4>
              <p className="text-xs text-slate-400 leading-relaxed">
                Special 30-day zero-commission onboarding for early merchant partners.
              </p>
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
          <button
            type="button"
            onClick={handleCopyLink}
            className="w-full sm:w-auto px-6 py-3.5 rounded-2xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs shadow-lg shadow-emerald-600/30 flex items-center justify-center gap-2.5 transition-all"
          >
            {copied ? (
              <>
                <CheckCircle2 className="w-4 h-4 text-white" />
                <span>Link Copied To Clipboard!</span>
              </>
            ) : (
              <>
                <Copy className="w-4 h-4" />
                <span>Copy Our Launch Link for tracking your spot daily</span>
              </>
            )}
          </button>

          <Link
            to="/"
            className="w-full sm:w-auto px-6 py-3.5 rounded-2xl border border-slate-700 hover:border-slate-500 text-slate-300 font-bold text-xs flex items-center justify-center gap-2 transition-all"
          >
            <span>Explore MarketMate Website</span>
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Launch;
